import { modifier } from 'ember-modifier'
import ScrollBooster from 'scrollbooster'

interface Signature {
  Args: {
    Named: {
      contentSelector: string
      setPageNav?: (pageLeft: null | (() => void), pageRight: null | (() => void)) => void
    }
    Positional: unknown[]
  }
  Element: HTMLElement
}

export default modifier<Signature>(function scrollDrag(element, _params, hash) {
  const { contentSelector, setPageNav } = hash

  const viewport = element
  const content = element.querySelector<HTMLElement>(contentSelector)
  if (!content) return () => {}

  const boost = new ScrollBooster({
    viewport,
    content,
    scrollMode: 'native',
    direction: 'horizontal',
    onClick: (state, event) => {
      if (state.isDragging) {
        event.preventDefault()
      }
    },
  }) as ScrollBooster & { events: { click: () => void }; _hasPages?: boolean }
  element.style.userSelect = 'none'
  element.style.cursor = 'grab'

  /* While dragging, need to prevent click handlers of elements within the scroll area from firing
   *
   * Open issue: https://github.com/ilyashubin/scrollbooster/issues/39
   * The scrollbooster's click handler should be handling this..
   * https://github.com/ilyashubin/scrollbooster/blob/31ee2d16b5df5dbe7abc6475abdffc58bd6a6e6e/src/index.js#L595-L598
   * But the event listener isn't wired up to `capture: true`, so it doesn't get called _before_ events are dispatched
   * to listeners on targets lower down in the DOM tree.
   * https://github.com/ilyashubin/scrollbooster/blob/31ee2d16b5df5dbe7abc6475abdffc58bd6a6e6e/src/index.js#L607
   *
   * For now, we can capture those events and forward them on to the same handler.
   */
  element.addEventListener('click', boost.events.click, true)

  const pageLeft = () => {
    const elementWidth = element.offsetWidth
    const state = boost.getState()
    const { x } = state.position
    const newX = x - elementWidth // a page is the size of the viewport width
    const minX = 0
    boost.scrollTo({ x: newX < minX ? minX : newX })
  }
  const pageRight = () => {
    const elementWidth = element.offsetWidth
    const state = boost.getState()
    const { x } = state.position
    const newX = x + elementWidth // a page is the size of the viewport width
    const maxX = element.scrollWidth - elementWidth
    boost.scrollTo({ x: newX > maxX ? maxX : newX })
  }

  const checkWidth = (el: Element) => {
    // Check if the width indicates that there are pages to scroll. If this changed, call the setPageNav callback
    if (el.scrollWidth > el.clientWidth) {
      if (!boost._hasPages) setPageNav?.(pageLeft, pageRight)
      boost._hasPages = true
    } else {
      if (boost._hasPages) setPageNav?.(null, null)
      boost._hasPages = false
    }
  }

  // Check if ResizeObserver supported (not supported by Safari 12)
  let resizeObserver: ResizeObserver | undefined
  if ('ResizeObserver' in window) {
    // When there are pages, call the setPageNav callback to set the page nav functions
    resizeObserver = new ResizeObserver((entries) => checkWidth(entries[0].target))
    resizeObserver.observe(element)
  } else {
    // Just always show the page nav buttons
    setPageNav?.(pageLeft, pageRight)
  }

  return () => {
    resizeObserver?.disconnect()
    element.removeEventListener('click', boost.events.click, true)
    boost.destroy()
  }
})
